import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import Button from 'core/elements/button/Button'
import Modal from 'core/elements/modal/Modal'
import Theme from 'core/themes/model'
import React, { useEffect, useCallback, useState, useMemo } from 'react'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import Progress from 'core/components/progress/Progress'
import StatusBanner from 'core/components/StatusBanner'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { resmgrHostsByIdSelector } from 'openstack/components/infrastructure/selectors'
import { cinderMessagesForVolumeSelector, cinderMessageSelector } from '../messages/selectors'
import { listCinderMessagesForVolume, getCinderSingleMessage } from '../messages/actions'
import NumberedStepLabel from 'core/components/validatedForm/NumberedStepLabel'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import { listVolumeTypes } from 'openstack/components/storage/volume-types/actions'
import { volumeTypesSelector } from 'openstack/components/storage/volume-types/selectors'
import { getBlueprints } from 'openstack/components/infrastructure/actions'
import { Blueprint } from 'openstack/components/infrastructure/blueprint/model'
import { keys } from 'ramda'
import ExternalLink from 'core/components/ExternalLink'
import { volumeStorageRequirements } from 'app/docLinks'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  errorIcon: {
    color: theme.palette.red[500],
    marginRight: 8,
  },
  errorBlock: {
    color: theme.palette.red[900],
  },
  keyValuePairs: {
    display: 'flex',
  },
  key: {
    fontWeight: 'bold',
    color: theme?.components?.typography?.default,
    marginRight: theme.spacing(1),
  },
  value: {
    wordBreak: 'break-word',
    color: theme?.components?.typography?.default,
  },
  moreDetails: {
    display: 'inline-flex',
    gap: 4,
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover > span': {
      textDecoration: 'underline',
    },
  },
  icon: {
    fontFamily: '"Font Awesome 6 Pro"',
    fontSize: 14,
  },
  info: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'max-content auto',
  },
  infoBox: {
    display: 'grid',
    borderRadius: 4,
    padding: '24px',
    gap: 16,
    border: `1px solid ${theme.palette.blue[500]}`,
    background: theme.palette.blue[100],
  },
  errorBox: {
    display: 'grid',
    borderRadius: 4,
    padding: '24px',
    gap: 16,
    border: `1px solid ${theme.palette.red[500]}`,
    background: theme.palette.red[100],
  },
  splitColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    alignItems: 'center',
  },
  troubleshootingStep: {
    '& .title': {
      ...theme.typography.body2,
    },
  },
}))

// Parse additionalInfo string into key-value pairs
const parseAdditionalInfo = (additionalInfo: string, hostsById, backend) => {
  if (!additionalInfo) return []

  return additionalInfo
    .split(';')
    .map((pair) => {
      const trimmedPair = pair.trim()
      if (!trimmedPair) return null

      const equalIndex = trimmedPair.indexOf('=')
      if (equalIndex === -1) {
        return { key: null, value: trimmedPair }
      }

      const key = trimmedPair.substring(0, equalIndex).trim()
      const value = trimmedPair.substring(equalIndex + 1).trim()

      // Skip if either key or value is not available
      if (!key || !value) return null

      // Special value display for backend & cinder_host keys
      const hostIds = keys(hostsById)
      const hostId = key === 'cinder_host' ? hostIds.find((id: string) => value.includes(id)) : null
      const alteredValue =
        key === 'backend' && backend
          ? `${backend} (${value})`
          : hostId
          ? `${hostsById?.[hostId]?.name} (${value})`
          : value

      return { key, value: alteredValue }
    })
    .filter(Boolean)
}

interface ErrorDialogProps {
  volume: any // For typing add this in after adding typing for volume selector
  onClose: () => void
}

interface ErrorType {
  type: string
  subtype: string
}

const parseErrorType: (message: string, backend: string) => ErrorType = (message, backend) => {
  if (!message) {
    return {
      type: 'other',
      subtype: null,
    }
  }
  const type = message.includes('Cannot create volume')
    ? 'create'
    : message.includes('Cannot retype volume')
    ? 'retype'
    : message.includes('Cannot migrate volume')
    ? 'migrate'
    : 'other'
  if (message.includes("Parameter 'size_gb' must fit")) {
    return {
      type: type,
      subtype: 'capacity',
    }
  } else if (message.includes("Parameter 'volume_type' requirement")) {
    return {
      type: type,
      subtype: 'volumeType',
    }
  } else if (message.includes('Temporary image conversion directory')) {
    return {
      type: type,
      subtype: 'imageConversionDirectory',
    }
  } else if (/Storage backend .* is out of space/.test(message)) {
    return {
      type: type,
      subtype: 'noSpace',
    }
  } else {
    return {
      type: type,
      subtype: null,
    }
  }
}

const VolumeErrorModal = ({ volume, onClose }: ErrorDialogProps) => {
  const classes = useStyles()
  const [detailedMessageId, setDetailedMessageId] = useState<string | null>(null)

  const { loading: loadingHosts } = useListAction(listResmgrHosts)
  const hostsById = useSelector(resmgrHostsByIdSelector)

  const { loading: fetchingMessages, reload: reloadMessages } = useListAction(
    listCinderMessagesForVolume,
    {
      params: { volumeUuid: volume?.id },
      loadOnDemand: true,
    },
  )

  const { loading: fetchingDetailedMessage, reload: reloadDetailedMessage } = useListAction(
    getCinderSingleMessage,
    {
      params: { messageId: detailedMessageId, resourceUuid: volume?.id },
      loadOnDemand: true,
    },
  )
  const isLoading = fetchingMessages || fetchingDetailedMessage

  const messages = useSelectorWithParams(cinderMessagesForVolumeSelector, {
    volumeUuid: volume?.id,
  })

  const detailedMessage = useSelector(cinderMessageSelector)

  const volumeMessage = useMemo(() => {
    if (!messages || messages.length === 0) return null
    return (
      messages.find((msg) => msg?.resource_type === 'VOLUME' && msg?.message_level === 'ERROR') ||
      messages[0]
    )
  }, [messages])

  // Fetch detailed message when we have a message ID
  useEffect(() => {
    if (volumeMessage?.id && volumeMessage?.id !== detailedMessageId) {
      setDetailedMessageId(volumeMessage.id)
    } else if (!volumeMessage && detailedMessageId) {
      // Clear detailedMessageId when there are no messages
      setDetailedMessageId(null)
    }
  }, [volumeMessage?.id, detailedMessageId])

  // Reload detailed message when ID changes
  useEffect(() => {
    if (detailedMessageId) {
      reloadDetailedMessage(true)
    }
  }, [detailedMessageId])

  const [copied, setCopied] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const [currentBlueprint, setCurrentBlueprint] = useState<Blueprint>({} as Blueprint)

  const { message, loading, reload } = useListAction(listVolumeTypes)
  const volumeTypes = useSelector(volumeTypesSelector)

  const matchedVolumeType = useMemo(() => {
    const match = volumeTypes?.find((type) => volume?.volume_type === type?.name)
    return match
  }, [volumeTypes, volume])

  const user_message = detailedMessage?.user_message || volumeMessage?.user_message
  const additional_info = detailedMessage?.additional_info
  const matchedBackend = matchedVolumeType?.extra_specs?.volume_backend_name

  const errorType = parseErrorType(user_message, volume?.backend)

  // Load blueprint and volume types when component mounts
  useEffect(() => {
    const makeCalls = async () => {
      const blueprints = await getBlueprints()
      setCurrentBlueprint(blueprints?.[0])
    }
    makeCalls()
  }, [])

  const configsByBackend = useMemo(() => {
    if (!currentBlueprint) {
      return {}
    }
    const backendKeys = keys(currentBlueprint?.storageBackends)
    return backendKeys?.reduce((accum, backend) => {
      const configKeys = keys(currentBlueprint.storageBackends[backend])
      return {
        ...accum,
        [backend]: configKeys.map((key) => ({
          ...currentBlueprint?.storageBackends?.[backend]?.[key],
          name: key,
        })),
      }
    }, {})
  }, [currentBlueprint])

  useEffect(() => {
    if (volume?.id) {
      reloadMessages(true)
    }
  }, [volume?.id])

  // Close on Esc
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleErrorCopy = useCallback(() => {
    navigator.clipboard.writeText(`${user_message}

${additional_info}`)
    setCopied(true)
  }, [user_message, additional_info])

  return (
    <Modal
      title={
        <div>
          <FontAwesomeIcon className={classes.errorIcon} solid>
            circle-x
          </FontAwesomeIcon>
          {errorType?.type === 'create'
            ? 'Volume Creation Failed'
            : errorType?.type === 'migrate'
            ? 'Volume Migration Failed'
            : errorType?.type === 'retype'
            ? 'Volume Retype Failed'
            : 'Volume Error Details'}
        </div>
      }
      onClose={onClose}
      maxWidth={1000}
      panel="dialog"
      footer={
        <>
          {user_message && (
            <Button variant="secondary" onClick={handleErrorCopy}>
              {copied ? 'Copied!' : 'Copy Error'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
      open
    >
      {isLoading ? (
        <Progress loading={isLoading} message="Loading Volume Error..." />
      ) : user_message ? (
        <StatusBanner variant="error" className={classes.errorBlock}>
          {user_message
            .split('.')
            .filter(Boolean)
            .map((line, index) => (
              <div key={index}>{line}.</div>
            ))}
          <br />
          {additional_info ? (
            <>
              {showMore ? (
                <>
                  {parseAdditionalInfo(additional_info, hostsById, matchedBackend).map(
                    (pair, index) => (
                      <div className={classes.keyValuePairs} key={index}>
                        {pair.key ? <div className={classes.key}>{pair.key}:</div> : null}
                        <div className={classes.value}>{pair.value}</div>
                      </div>
                    ),
                  )}
                  <br />
                  <Text
                    variant="body2"
                    className={classes.moreDetails}
                    onClick={() => setShowMore(false)}
                  >
                    <span>See Less</span>{' '}
                    <FontAwesomeIcon className={classes.icon}>chevron-up</FontAwesomeIcon>
                  </Text>
                </>
              ) : (
                <Text
                  variant="body2"
                  className={classes.moreDetails}
                  onClick={() => setShowMore(true)}
                  data-testid="show-more-button"
                >
                  <span>More Details</span>{' '}
                  <FontAwesomeIcon className={classes.icon}>chevron-down</FontAwesomeIcon>
                </Text>
              )}
            </>
          ) : null}
        </StatusBanner>
      ) : (
        <StatusBanner variant="error" className={classes.errorBlock}>
          Cannot create volume '<b>{volume?.name || volume?.id}</b>'.
          <br />
          <br />
          <div>No error details are available for this volume. </div>
          <div>Error details may take some time to surface.</div>
        </StatusBanner>
      )}
      {!isLoading && (
        <>
          <Text variant="subtitle2">Configuration Details</Text>
          <div className={classes.infoBox}>
            <div className={classes.info}>
              <Text variant="caption1">Requested Volume Type:</Text>
              <Text variant="body2">{volume?.volume_type}</Text>
              <Text variant="caption1">Volume Type Metadata:</Text>
              <Labels
                labels={matchedVolumeType?.extra_specs}
                variant="default"
                showMoreButton
                ellipsisAt={35}
              />
            </div>
          </div>
          {isAdmin() && errorType?.subtype === 'volumeType' && (
            <>
              <Text variant="subtitle2">Associated Volume Backend Configurations</Text>
              {configsByBackend?.[matchedBackend]?.map((config) => (
                <div className={classes.errorBox} key={config?.name}>
                  <div className={classes.splitColumn}>
                    <Text variant="body2">
                      <b>Name:</b> {config?.name}
                    </Text>
                    <Text variant="body2">
                      <b>Driver:</b> {config?.driver}
                    </Text>
                  </div>
                  <div className={classes.info}>
                    <Text variant="caption1">Configs:</Text>
                    <Labels
                      labels={config?.config}
                      variant="default"
                      showMoreButton
                      ellipsisAt={35}
                    />
                  </div>
                </div>
              ))}
            </>
          )}
          {isAdmin() ? (
            errorType?.subtype && (
              <>
                <Text variant="subtitle2">What to do next</Text>
                {errorType.subtype === 'capacity' ? (
                  <div className={classes.infoBox}>
                    <NumberedStepLabel
                      step={1}
                      title={`Make space by increasing storage capacity on ${volume?.backend}, or use a
                      different volume type that matches a backend with more capacity`}
                      className={classes.tutorialStep}
                      bold={false}
                    />
                    <NumberedStepLabel
                      step={2}
                      title={
                        <>
                          Read{' '}
                          <ExternalLink url={volumeStorageRequirements}>this article</ExternalLink>{' '}
                          for more troubleshooting information
                        </>
                      }
                      className={classes.tutorialStep}
                      bold={false}
                    />
                  </div>
                ) : errorType.subtype === 'volumeType' ? (
                  <div className={classes.infoBox}>
                    <NumberedStepLabel
                      step={1}
                      title="Select a different volume type that matches the capabilities of the available
                      volume backend configurations displayed above."
                      className={classes.tutorialStep}
                      bold={false}
                    />
                    <NumberedStepLabel
                      step={2}
                      title={
                        <>
                          Read{' '}
                          <ExternalLink url={volumeStorageRequirements}>this article</ExternalLink>{' '}
                          for more troubleshooting information
                        </>
                      }
                      className={classes.tutorialStep}
                      bold={false}
                    />
                  </div>
                ) : errorType.subtype === 'imageConversionDirectory' ? (
                  <div className={classes.infoBox}>
                    <NumberedStepLabel
                      step={1}
                      title="Make sure that you have sufficient storage space configured for this directory
                      on the host with persistent storage role assigned."
                      className={classes.tutorialStep}
                      bold={false}
                    />
                    <NumberedStepLabel
                      step={2}
                      title={
                        <>
                          Read{' '}
                          <ExternalLink url={volumeStorageRequirements}>this article</ExternalLink>{' '}
                          for more troubleshooting information
                        </>
                      }
                      className={classes.tutorialStep}
                      bold={false}
                    />
                  </div>
                ) : errorType.subtype === 'noSpace' ? (
                  <div className={classes.infoBox}>
                    <NumberedStepLabel
                      step={1}
                      title="Make space by deleting unused volumes on this storage backend, or contact your
                      storage administrator to increase capacity for the configured storage pool."
                      className={classes.tutorialStep}
                      bold={false}
                    />
                    <NumberedStepLabel
                      step={2}
                      title={
                        <>
                          Read{' '}
                          <ExternalLink url={volumeStorageRequirements}>this article</ExternalLink>{' '}
                          for more troubleshooting information
                        </>
                      }
                      className={classes.tutorialStep}
                      bold={false}
                    />
                  </div>
                ) : null}
              </>
            )
          ) : user_message ? (
            <>
              <Text variant="subtitle2">What to do next</Text>
              <div className={classes.infoBox}>
                <NumberedStepLabel
                  step={1}
                  title={
                    <>
                      Contact your administrator for assistance and provide the full error message
                      by pressing <b>Copy Error</b> below
                    </>
                  }
                  className={classes.tutorialStep}
                  bold={false}
                />
                <NumberedStepLabel
                  step={2}
                  title={
                    <>
                      Read <ExternalLink url={volumeStorageRequirements}>this article</ExternalLink>{' '}
                      for more troubleshooting information
                    </>
                  }
                  className={classes.tutorialStep}
                  bold={false}
                />
              </div>
            </>
          ) : null}
        </>
      )}
    </Modal>
  )
}

export default VolumeErrorModal
